import {Injectable} from "@angular/core";
import {VizixService} from "./vizix.service";
import {SQLite, SQLiteObject} from "@ionic-native/sqlite";
import {Platform} from "ionic-angular";
import {Network} from "@ionic-native/network";
import {EncodedTag} from "../models/EncodedTag";
import {VizixEventService} from "./vizix.event.service";
import {PatchThingRequestItem} from "../models/PatchThingRequestItem";
import {
  ITEM_EVENT_ENCODE_ASSOCIATION_TAG,
  ITEM_UDF_ACTIVITY,
  ITEM_UDF_CATEGORY,
  ITEM_UDF_SOURCE,
  ITEM_UDF_SUB_CATEGORY,
  ITEM_UDF_TS_REASSOCIATED,
  ITEM_UDF_TS_REASSOCIATED_DATE,
  ITEM_UDF_USER,
  ITEM_UDF_UUID_MOBILE,
  VIZIX_EVENT_ID_CREATE_ENCODE_ASSOCIATION,
  VIZIX_THING_ITEM_UDF_SOURCE,
  VizixEventAction,
  VizixEventActivity,
  VizixEventCategory,
  VizixEventEventType,
  VizixEventSubCategory
} from "../app/app.constants";
import {Subscription} from "rxjs/Subscription";

@Injectable()
export class EncodeTagsService {

  private tag = '[ENCODE_TAGS_SERVICE]';

  private DB_NAME = 'EncodeTags.db';
  private DB_LOCATION = 'default';
  private DB_TABLE_ENCODE_TAGS = 'encodeTags';
  private db: SQLiteObject;
  private isConnected: boolean = false;
  private isCordova: boolean = false;
  private isCheckingPending: boolean = false;

  private TABLE_COLUMN_PRODUCT_SERIAL = 'productSerial';
  private TABLE_COLUMN_SECOND_FIELD = 'secondField';
  private TABLE_COLUMN_NEW_EPC = 'newEpc';
  private TABLE_COLUMN_SELECTED_ZONE = 'selectedZone';
  private TABLE_COLUMN_DATE = 'date';

  private disconnectedSubscription: Subscription;
  private connectedSubscription: Subscription;

  constructor(public platform: Platform,
              private network: Network,
              private vizixService: VizixService,
              private sqlite: SQLite,
              private vizixEventService: VizixEventService) {
  }

  /**
   * Initializes the database (if not exists) and starts the service
   */
  public init(skipUpload?: boolean) {
    console.log(this.tag, 'Initializing');

    // Init database and table if table not exists
    //  Encoded Tags Table columns:
    //    productSerial | secondField | newEpc | selectedZone | date

    if (this.platform.is('cordova') && !this.platform.is('core')) {
      this.isCordova = true;
      console.log(this.tag, 'Platform is mobile, init db');

      this.sqlite.create({name: this.DB_NAME, location: this.DB_LOCATION}).then((db: SQLiteObject) => {
        this.db = db;
        this.db.executeSql('create table if not exists ' + this.DB_TABLE_ENCODE_TAGS +
          '(' + this.TABLE_COLUMN_PRODUCT_SERIAL + ' VARCHAR(32), ' +
          this.TABLE_COLUMN_SECOND_FIELD + ' VARCHAR(64), ' +
          this.TABLE_COLUMN_NEW_EPC + ' VARCHAR(64), ' +
          this.TABLE_COLUMN_SELECTED_ZONE + ' VARCHAR(64), ' +
          this.TABLE_COLUMN_DATE + ' INT)', {})
          .then(() => {
            console.log(this.tag, 'table ' + this.DB_TABLE_ENCODE_TAGS + ' created');
            this.initService();
          })
          .catch(e => {
            console.log(this.tag, e);
            this.isCordova = false;
          });
      }).catch(e => console.log(this.tag, e));
    } else {
      console.log(this.tag, 'Platform is not mobile, skipping database');
      this.isCordova = false;
      this.initService();
    }
  }

  public deleteDataBase() {
    if (this.isCordova) {
      this.sqlite.deleteDatabase({name: this.DB_NAME, location: this.DB_LOCATION}).then(() => {
        console.log(this.tag, 'Delete DB Encode Tgs successful');
      }, err_ => {
        console.log(this.tag, 'ERROR Delete DB Encode Tgs');
      });
    } else {
      console.log(this.tag, 'Platform is not cordova! Cant delete database');
    }
  }


  /**
   * Starts the service, checks for pending Encoded Tgs to upload
   * It is aware of network connection
   */
  private initService(skipUpload?: boolean) {

    // Check if network connection is available
    if (this.network.type != 'none' && this.network.type != 'unknown') {
      // If available, then check if pending Encoded Tgs
      console.log(this.tag, 'network state: ' + this.network.type);
      this.isConnected = true;

      if (!skipUpload) {
        setTimeout(() => { // Wait 5 seconds on service startup to check if pending items (to init db)
          this.checkPending();
        }, 5000);
      }

    } else {
      console.log(this.tag, 'Not connected, waiting for network connection availability');
    }

    // Called when network changes from disconnected to connected
    if(!this.connectedSubscription) {
      this.connectedSubscription = this.network.onConnect().subscribe(() => {
        console.log(this.tag, 'network connected!');
        this.isConnected = true;
        this.checkPending();
      });
    }

    // Called when network changes from connected to disconnected
    if(!this.disconnectedSubscription) {
      this.disconnectedSubscription = this.network.onDisconnect().subscribe(() => {
          console.log(this.tag, 'network disconnected!');
          this.isConnected = false;
        });
    }
  }

  /**
   * Uploads EncodeTags object to Vizix, if it fails, saves it locally
   * @param encodedTag
   * @param fromLocalDatabase
   */
  public saveEncodedTag(encodedTag: EncodedTag, fromLocalDatabase?: boolean): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      if (encodedTag.productSerial &&
        encodedTag.secondField &&
        encodedTag.selectedZone &&
        encodedTag.newEpc &&
        encodedTag.date) {

        let username: string;
        let uuid: string;
        let isError = false; // To control the events in case of error
        let errorMessage = null; // To control the events in case of error

        this.vizixEventService.getVizixEventItemBase().then(eventBase => {
          uuid = eventBase.remoteSource;  //uuid
          username = eventBase.user; //user
          //eventBase.eventDate; //date


          let body: PatchThingRequestItem[] = [];
          let requestItem = new PatchThingRequestItem();
          requestItem.timestamp = encodedTag.date;
          requestItem.serialNumber = encodedTag.newEpc;
          requestItem.group = this.vizixService.configurationApp.AppParametersConfiguration.Group.value;
          requestItem.name = encodedTag.newEpc;
          requestItem.thingTypeCode = this.vizixService.configurationApp.EncodeTagConfiguration.ThingTypeCodeToSync.value;

          requestItem.udfs = {};
          requestItem.udfs[this.vizixService.configurationApp.EncodeTagConfiguration.EncodeZoneUDF.value] = {value: String(this.vizixService.getStoreId()) + '_' + encodedTag.selectedZone.zoneTypeCode};
          requestItem.udfs[this.vizixService.configurationApp.AppParametersConfiguration.ItemEventUDF.value] = {value: ITEM_EVENT_ENCODE_ASSOCIATION_TAG};
          // TODO: MOVE TO DATA ENTRY

          requestItem.udfs[ITEM_UDF_TS_REASSOCIATED] = {value: true};
          requestItem.udfs[ITEM_UDF_TS_REASSOCIATED_DATE] = {value: this.vizixService.vizixTime()};
          requestItem.udfs[ITEM_UDF_USER] = {value: username};
          requestItem.udfs[ITEM_UDF_UUID_MOBILE] = {value: uuid};
          requestItem.udfs[ITEM_UDF_SOURCE] = {value: VIZIX_THING_ITEM_UDF_SOURCE};
          requestItem.udfs[ITEM_UDF_CATEGORY] = {value: 'Workflow'};
          requestItem.udfs[ITEM_UDF_SUB_CATEGORY] = {value: 'Manage Tags'};
          requestItem.udfs[ITEM_UDF_ACTIVITY] = {value: 'Encode Tag'};
          requestItem.udfs[this.vizixService.configurationApp.EncodeTagConfiguration.ItemUPCUDF.value] = {value: encodedTag.productSerial};
          requestItem.udfs[this.vizixService.configurationApp.EncodeTagConfiguration.OriginalValueUDF.value] = {value: encodedTag.secondField};

          body.push(requestItem);

          // TODO: MOVE VIZIX SERVICE PATCH METHOD
          this.generateAndSaveVizixEvent(encodedTag, VizixEventActivity.ENCODE_TAG, VIZIX_EVENT_ID_CREATE_ENCODE_ASSOCIATION, this.vizixService.configurationApp.EncodeTagConfiguration.ThingTypeCodeToSync.value, false, "", VizixEventAction.START);
          this.vizixService.VizixPatchItem(
            body,
            {'upsert': 'true'}
          ).subscribe((data) => {
            if (data && data[encodedTag.secondField] && data[encodedTag.secondField].ERROR) {
              console.log(this.tag, 'Error in patch item, saving locally', data);
              reject();
            } else {
              console.log(this.tag, 'Encoded Tags recorded in Vizix');
              resolve(true);
            }
          }, (error) => {
            isError = true;
            errorMessage = error;
            console.log(this.tag, 'Error in patch item, saving locally', error);
            if (!fromLocalDatabase) this.saveEncodedTagLocally(encodedTag);
            resolve(false);
          }, () => {
            if (!isError) {
              this.generateAndSaveVizixEvent(encodedTag, VizixEventActivity.ENCODE_TAG, VIZIX_EVENT_ID_CREATE_ENCODE_ASSOCIATION, this.vizixService.configurationApp.EncodeTagConfiguration.ThingTypeCodeToSync.value, false, "", VizixEventAction.END);
            } else {
              this.generateAndSaveVizixEvent(encodedTag, VizixEventActivity.ENCODE_TAG, VIZIX_EVENT_ID_CREATE_ENCODE_ASSOCIATION, this.vizixService.configurationApp.EncodeTagConfiguration.ThingTypeCodeToSync.value, true, errorMessage);
            }
          });
        });

      } else {
        console.log(this.tag, 'Invalid encoded tag!');
        reject();
      }
    });
  }

  /**
   * Saves EncodeTags object to database (as String)
   * @param encodedTag
   */
  private saveEncodedTagLocally(encodedTag: EncodedTag) {
    if (this.isCordova) {
      this.db.executeSql('INSERT INTO ' + this.DB_TABLE_ENCODE_TAGS + ' VALUES(?,?,?,?,?)',
        [encodedTag.productSerial,
          encodedTag.secondField,
          encodedTag.newEpc,
          JSON.stringify(encodedTag.selectedZone),
          encodedTag.date])
        .then(res => {
          console.log(this.tag, 'Inserted', encodedTag);
        }).catch((error) => {
        console.log(this.tag, 'Error inserting', encodedTag);
      })
    } else {
      console.log(this.tag, 'Platform is not cordova! Cant save Encoded Tag locally');
    }
  }

  /**
   * Checks if there are Encoded Tags saved locally to upload
   */
  private checkPending() {
    if (!this.isCheckingPending) {
      this.isCheckingPending = true;
      if (this.isCordova) {
        if (this.isConnected) {
          console.log(this.tag, 'checking pending');
          // Try to upload Encoded Tags information from db.
          // it is not necessary to group them, just upload
          this.db.executeSql('select * from ' + this.DB_TABLE_ENCODE_TAGS, {})
            .then(res => {
              console.log(this.tag, 'Encoded Tags to upload: ' + res.rows.length);
              // Convert response from sql to list of EncodeTags

              let encodedTagsList: EncodedTag[] = [];
              for (let i = 0; i < res.rows.length; i++) {
                console.log(this.tag, 'Processing encoded tag', res.rows.item(i));
                if (res.rows.item(i)[this.TABLE_COLUMN_PRODUCT_SERIAL]) {
                  encodedTagsList.push(new EncodedTag(
                    res.rows.item(i)[this.TABLE_COLUMN_SECOND_FIELD],
                    res.rows.item(i)[this.TABLE_COLUMN_PRODUCT_SERIAL],
                    res.rows.item(i)[this.TABLE_COLUMN_NEW_EPC],
                    JSON.parse(res.rows.item(i)[this.TABLE_COLUMN_SELECTED_ZONE]),
                    res.rows.item(i)[this.TABLE_COLUMN_DATE]
                  ));
                } else {
                  console.log(this.tag, 'Invalid encoded tag from database!', res.rows);
                }
              }

              console.log(this.tag, 'Encoded Tag list', encodedTagsList);

              if (encodedTagsList.length > 0) {
                for (let encodedTag of encodedTagsList) {
                  this.saveEncodedTag(encodedTag, true).then((save) => {
                    if (save) {
                      this.removeEncodedTagFromDB(encodedTag);
                      this.isCheckingPending = false;
                    }
                  });
                }
              } else {
                this.isCheckingPending = false;
                console.log(this.tag, 'No pending Encoded Tags to upload');
              }

            }).catch(e => console.log(this.tag, 'Error getting Encoded Tags from database'));

        } else {
          console.log(this.tag, 'Not connected! waiting for internet connection');
        }
      } else {
        console.log(this.tag, 'Platform is not cordova! Cant check pending Encoded Tags');
      }
    } else {
      console.log(this.tag, 'Service is already checking pending Encoded Tags to upload');
    }
  }

  /**
   * Once uploaded successfully, the local Encoded Tag is removed from db
   * @param encodedTag
   */
  private removeEncodedTagFromDB(encodedTag: EncodedTag) {
    if (this.isCordova) {
      this.db.executeSql("DELETE FROM " + this.DB_TABLE_ENCODE_TAGS +
        " WHERE " + this.TABLE_COLUMN_PRODUCT_SERIAL + " = '" + encodedTag.productSerial +
        "' AND " + this.TABLE_COLUMN_SECOND_FIELD + " = '" + encodedTag.secondField +
        "' AND " + this.TABLE_COLUMN_NEW_EPC + " = '" + encodedTag.newEpc +
        "' AND " + this.TABLE_COLUMN_SELECTED_ZONE + " = '" + JSON.stringify(encodedTag.selectedZone) +
        "' AND " + this.TABLE_COLUMN_DATE + " = '" + String(encodedTag.date) + "'", [])
        .then(res => {
          console.log(this.tag, 'Removed Encoded Tag from db ', res);
        })
        .catch(e => {
          console.log(this.tag, e);
        });
    } else {
      console.log(this.tag, 'Platform is not cordova! Cant remove Encoded Tag');
    }
  }

  /**
   * Generate and save vizix event
   *
   * @param {VizixEventActivity} activity
   * @param {string} eventId
   * @param {string} thingTypeCode
   * @param {string} firstFieldText
   * @param {string} secondFieldText
   * @param {boolean} isError
   * @param {string} errorMessage
   */
  private generateAndSaveVizixEvent(encodedTag: EncodedTag,
                                    activity: VizixEventActivity,
                                    eventId: string,
                                    thingTypeCode: string,
                                    isError?: boolean,
                                    errorMessage?: string,
                                    action?: VizixEventAction) {
    this.vizixEventService.getVizixEventItemBase(isError).then((vizixEvent) => {
      vizixEvent.category = VizixEventCategory.WORKFLOW;
      vizixEvent.subCategory = VizixEventSubCategory.ENCODE_TAG;
      vizixEvent.eventType = VizixEventEventType.INFO;
      vizixEvent.activity = activity;
      if (action) {
        vizixEvent.action = action;
      }
      if (isError) {
        vizixEvent.detail = this.vizixEventService.generateDetailsMessage(errorMessage,
          thingTypeCode, thingTypeCode, this.vizixService.configurationApp.AppParametersConfiguration.DefaultDepartment.value, encodedTag.selectedZone.name);
      } else {

        let detailsMessage = {
          product: encodedTag.productSerial,
          oldItem: encodedTag.secondField,
          item: encodedTag.newEpc
        };
        vizixEvent.detail = this.vizixEventService.generateDetailsMessage(detailsMessage,
          thingTypeCode, thingTypeCode, this.vizixService.configurationApp.AppParametersConfiguration.DefaultDepartment.value, encodedTag.selectedZone.name);

      }

      vizixEvent.count = 1;
      vizixEvent.eventId = eventId;
      this.vizixEventService.recordEvent(vizixEvent);
    }, (error) => {
      console.log(this.tag, 'Error getting VizixItemBase', error);
    });
  }
}



// WEBPACK FOOTER //
// ./src/providers/encode.tags.service.ts